import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { humanize } from '@pesto/utils/src/string/stringHelper';
import { z } from 'zod';

import type { CardModalPayload, ModalProps } from '../types/modal.types';

import { ReplaceCardType, useGetUserQuery, useReplaceCardMutation } from '../../__generated__/graphql/api';
import { getUserRefetchQuery } from '../../components/UserDetails/getUserRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';
import { YES_NO_OPTIONS } from '../utils/form-helpers';

const schema = z.object({
  replaceCardType: z.nativeEnum(ReplaceCardType, { required_error: 'Replace card type is required' }),
  useNewShippingAddress: z.string(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
});

export const ReplaceCardActionComponent = ({ payload, api }: ModalProps<CardModalPayload>) => {
  const { userId = '', cardReferenceId } = payload;

  const { data } = useGetUserQuery({
    variables: {
      userId,
    },
    skip: !userId || !!cardReferenceId,
  });

  const user = data?.user;
  const activeCard = data?.user?.cards?.find((card) => card?.active);
  const referenceId = cardReferenceId ?? activeCard?.referenceId ?? '';
  const resolvedUserId = userId || user?.id || '';

  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getUserRefetchQuery(userId);
  const [mutationAction, { loading }] = useReplaceCardMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        mutationAction({
          variables: {
            userId: resolvedUserId,
            referenceId,
            replaceCardType: data.replaceCardType,
            ...(data.useNewShippingAddress === 'true' && {
              addressInput: {
                addressFirstLine: data.addressLine1 ?? '',
                addressSecondLine: data.addressLine2 ?? '',
                city: data.city ?? '',
                state: data.state ?? '',
                zip: data.zip ?? '',
              },
            }),
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'replace-card',
      toast: {
        success: 'Card replaced successfully',
        error: 'Failed to replace card',
        loading: 'Replacing card...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fieldGroups: [
      {
        name: 'cardDetails',
        label: 'Card Information',
        fields: [
          {
            name: 'replaceCardType',
            label: 'Replace Card Type',
            type: 'select',
            required: true,
            defaultValue: ReplaceCardType.Lost,
            options: Object.values(ReplaceCardType).map((type) => ({
              value: type,
              label: humanize(type),
            })),
          },
          {
            name: 'useNewShippingAddress',
            label: 'Use New Shipping Address',
            type: 'select',
            required: true,
            defaultValue: false,
            options: YES_NO_OPTIONS,
          },
        ],
      },
      {
        name: 'shippingAddress',
        label: 'Shipping Address',
        description: 'Enter the new shipping address for the replacement card',
        conditional: {
          when: {
            field: 'useNewShippingAddress',
            operator: 'equals',
            value: 'true',
          },
        },
        fields: [
          {
            name: 'addressLine1',
            label: 'Address Line 1',
            type: 'text',
            required: true,
            defaultValue: '',
            placeholder: 'Enter street address',
          },
          {
            name: 'addressLine2',
            label: 'Address Line 2',
            type: 'text',
            required: false,
            defaultValue: '',
            placeholder: 'Apartment, suite, etc. (optional)',
          },
          {
            name: 'city',
            label: 'City',
            type: 'text',
            required: true,
            defaultValue: '',
            placeholder: 'Enter city',
          },
          {
            name: 'state',
            label: 'State',
            type: 'text',
            required: true,
            defaultValue: '',
            placeholder: 'Enter state',
          },
          {
            name: 'zip',
            label: 'Zip Code',
            type: 'text',
            required: true,
            defaultValue: '',
            placeholder: 'Enter zip code',
          },
        ],
      },
    ],
    schema,
    layout: 'vertical',
    defaultValues: {
      replaceCardType: ReplaceCardType.Replaced,
      useNewShippingAddress: 'false',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
